"use client";

import { CustomSelect } from "@shared/src/components/ui/common/form-components/CustomSelect";
import { forwardRef, useMemo } from "react";
import { useTokenBalance } from "@shared/src/hooks/useSingleBalance";
import { MIN_QUICK_TRADE_LIMIT, MAX_QUICK_TRADE_LIMIT } from "@shared/src/utils/constants";
import { FaArrowRight } from "react-icons/fa";
import { RotateCw, ArrowUpDown } from "lucide-react";

interface OptionItem {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface QuickTradeInputsProps {
  tradeType: "buy" | "sell";
  amount: string;
  setAmount: (value: string) => void;
  selectedNetwork: string;
  handleNetworkChange: (value: string | string[]) => void;
  networkOptions: OptionItem[];
  selectedFiatCurrency: string;
  handleFiatCurrencyChange: (value: string | string[]) => void;
  currencyOptions: OptionItem[];
  selectedToken: string;
  handleTokenChange: (value: string | string[]) => void;
  cryptoOptions: OptionItem[];
  error?: string;
  isPrimary?: boolean;
  onSwap?: () => void;
}

const BuyLayout = forwardRef<HTMLInputElement, Omit<QuickTradeInputsProps, "tradeType">>(
  (
    {
      amount,
      setAmount,
      selectedNetwork,
      handleNetworkChange,
      networkOptions,
      selectedToken: selectedCryptoCurrency,
      handleTokenChange: handleCryptoCurrencyChange,
      cryptoOptions,
      error,
      isPrimary,
      onSwap,
    },
    ref,
  ) => {
    const handleBuyChange = (value: string) => {
      setAmount(value);
    };

    const tokenLabel = cryptoOptions.find(c => c.value === selectedCryptoCurrency)?.label.split(" ")[0] || "";

    return (
      <div className="w-full space-y-4">
        {/* First Input - I want to buy (Crypto) */}
        <div className="border-2 border-border-tertiary bg-surfaces-bg-secondary rounded-5xl min-h-[120px] flex items-center px-4 md:px-6 font-manrope py-4 group has-[:focus]:border-border-default">
          <div className="flex flex-col w-full">
            <div className="flex-1 w-full">
              <span className="text-small font-extrabold whitespace-nowrap">You receive</span>
              <div className="flex items-center gap-2">
                {
                  <button
                    type="button"
                    onClick={onSwap}
                    disabled={!isPrimary || !onSwap}
                    className=" group/swap text-text-tertiary flex gap-1 items-center  hover:text-text-primary transition-colors"
                    aria-label="Swap input order"
                  >
                    {isPrimary && onSwap && (
                      <span className="text-text-brand hover:text-text-brandsoft  group-hover/swap:text-text-brandsoft transition-colors">
                        <ArrowUpDown size={20} />
                      </span>
                    )}{" "}
                    {tokenLabel}
                  </button>
                }
                <input
                  ref={ref}
                  type="number"
                  min="0"
                  step="any"
                  onKeyDown={e => {
                    if (e.key === "-" || e.key === "+") {
                      e.preventDefault();
                    }
                  }}
                  className="py-0.5 px-2 pl-0 text-h5 font-unbounded bg-transparent outline-none w-full"
                  placeholder="0.00"
                  value={amount}
                  onChange={e => handleBuyChange(e.target.value)}
                />
              </div>
            </div>
            {error && <p className="text-small text-text-error mt-1">{error}</p>}

            {/* Limits info */}
            <div className="flex items-center gap-4 text-small">
              <button
                type="button"
                className="text-text-brand font-bold hover:underline"
                onClick={() => setAmount(String(MAX_QUICK_TRADE_LIMIT))}
              >
                Max
              </button>
            </div>
            <div className="flex items-center gap-2 justify-end">
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1 flex-shrink-0 text-text-primary rounded-2xl py-2 px-1 bg-surfaces-bg-secondary-darker text-small justify-center">
                  <CustomSelect
                    label=""
                    hideLabel
                    value={selectedCryptoCurrency}
                    onValueChange={handleCryptoCurrencyChange}
                    options={cryptoOptions}
                    variant="dropdown"
                    layout="list"
                    containerClassName="!min-h-0 !h-auto !border-0 !bg-transparent !p-0 !m-0"
                    className="!min-h-0 !h-auto !border-0 !p-0 !m-0 !text-small"
                  />
                </div>
                <div className="relative w-fit">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center size-6 rounded-xl bg-surfaces-bg-secondary-darker border-2 border-border-secondary z-10 overflow-visible">
                    <FaArrowRight size={12} className="text-text-primary" />
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 text-text-primary rounded-2xl py-2 px-1 bg-surfaces-bg-secondary-darker text-small justify-center">
                  <CustomSelect
                    modal={false}
                    label=""
                    hideLabel
                    value={selectedNetwork}
                    onValueChange={handleNetworkChange}
                    options={networkOptions}
                    variant="dropdown"
                    layout="list"
                    containerClassName="!min-h-0 !h-auto !border-0 !bg-transparent !p-0 !m-0"
                    className="!min-h-0 !h-auto !border-0 !p-0 !m-0 !text-small"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

BuyLayout.displayName = "BuyLayout";

const SellLayout = forwardRef<HTMLInputElement, Omit<QuickTradeInputsProps, "tradeType">>(
  (
    {
      amount,
      setAmount,
      selectedNetwork,
      handleNetworkChange,
      networkOptions,

      selectedToken: selectedCryptoCurrency,
      handleTokenChange: handleCryptoCurrencyChange,
      cryptoOptions,
      error,
      isPrimary,
      onSwap,
    },
    ref,
  ) => {
    const handleSellChange = (value: string) => {
      setAmount(value);
    };

    const tokenAddress = useMemo(
      () => selectedCryptoCurrency.split("-")[0] as `0x${string}` | undefined,
      [selectedCryptoCurrency],
    );
    const { balance, refetch, isFetching } = useTokenBalance({ tokenAddress });
    const limitsText = `Limits: ${MIN_QUICK_TRADE_LIMIT} - ${MAX_QUICK_TRADE_LIMIT}`;
    const tokenLabel = cryptoOptions.find(c => c.value === selectedCryptoCurrency)?.label.split(" ")[0] || "";

    return (
      <div className="w-full space-y-4">
        {/* First Input - I want to sell (Crypto) */}
        <div className="border-2 border-border-tertiary bg-surfaces-bg-secondary rounded-5xl min-h-[120px] flex items-center px-4 md:px-6 font-manrope py-4 group has-[:focus]:border-border-default">
          <div className="flex flex-col w-full">
            <div className="flex-1 w-full">
              <span className="text-small font-extrabold whitespace-nowrap">You receive</span>
              <div className="flex items-center gap-2">
                {
                  <button
                    type="button"
                    onClick={onSwap}
                    disabled={!isPrimary || !onSwap}
                    className=" group/swap text-text-tertiary flex gap-1 items-center  hover:text-text-primary transition-colors"
                    aria-label="Swap input order"
                  >
                    {isPrimary && onSwap && (
                      <span className="text-text-brand hover:text-text-brandsoft  group-hover/swap:text-text-brandsoft transition-colors">
                        <ArrowUpDown size={20} />
                      </span>
                    )}{" "}
                    {tokenLabel}
                  </button>
                }
                <input
                  ref={ref}
                  type="number"
                  min="0"
                  step="any"
                  onKeyDown={e => {
                    if (e.key === "-" || e.key === "+") {
                      e.preventDefault();
                    }
                  }}
                  className="py-0.5 px-2 pl-0 text-h5 font-unbounded bg-transparent outline-none w-full"
                  placeholder="0.00"
                  value={amount}
                  onChange={e => handleSellChange(e.target.value)}
                />
              </div>
            </div>
            {error && <p className="text-small text-text-error mt-1">{error}</p>}
            <div className="flex items-center gap-4 text-small">
              <span className="flex items-center gap-2 text-text-tertiary">
                <span>Balance: {balance?.amount || "0"}</span>
                <button
                  type="button"
                  aria-label="Refresh balance"
                  onClick={() => refetch()}
                  className="text-text-tertiary hover:opacity-80"
                >
                  <RotateCw size={14} className={isFetching ? "animate-spin" : ""} />
                </button>
              </span>
              <button
                type="button"
                className="text-text-brand font-bold hover:underline"
                onClick={() => {
                  if (balance?.rawAmount) {
                    const raw = parseFloat(balance.rawAmount);
                    const fill = raw < MAX_QUICK_TRADE_LIMIT ? raw : MAX_QUICK_TRADE_LIMIT;
                    setAmount(String(fill));
                  }
                }}
              >
                All
              </button>
              {/* <button className="text-text-brand font-bold">Add Funds</button> */}
              <span className="text-text-tertiary">{limitsText}</span>
            </div>
            <div className="flex items-center gap-2 justify-end">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 flex-shrink-0 text-text-primary rounded-2xl py-2 px-1 bg-surfaces-bg-secondary-darker text-small justify-center">
                  <CustomSelect
                    label=""
                    hideLabel
                    value={selectedCryptoCurrency}
                    onValueChange={handleCryptoCurrencyChange}
                    options={cryptoOptions}
                    variant="dropdown"
                    layout="list"
                    containerClassName="!min-h-0 !h-auto !border-0 !bg-transparent !p-0 !m-0"
                    className="!min-h-0 !h-auto !border-0 !p-0 !m-0 !text-small"
                  />
                </div>
                <div className="relative w-fit">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center size-6 rounded-xl bg-surfaces-bg-secondary-darker border-2 border-border-secondary z-10 overflow-visible">
                    <FaArrowRight size={12} className="text-text-primary" />
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 text-text-primary rounded-2xl py-2 px-1 bg-surfaces-bg-secondary-darker text-small justify-center">
                  <CustomSelect
                    label=""
                    hideLabel
                    value={selectedNetwork}
                    onValueChange={handleNetworkChange}
                    options={networkOptions}
                    variant="dropdown"
                    layout="list"
                    containerClassName="!min-h-0 !h-auto !border-0 !bg-transparent !p-0 !m-0"
                    className="!min-h-0 !h-auto !border-0 !p-0 !m-0 !text-small"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

SellLayout.displayName = "SellLayout";

export const QuickTradeInputs = forwardRef<HTMLInputElement, QuickTradeInputsProps>(
  (
    {
      tradeType,
      amount,
      setAmount,
      selectedNetwork,
      handleNetworkChange,
      networkOptions,
      selectedFiatCurrency,
      handleFiatCurrencyChange,
      currencyOptions,
      selectedToken: selectedCryptoCurrency,
      handleTokenChange: handleCryptoCurrencyChange,
      cryptoOptions,
      error,
      isPrimary,
      onSwap,
    },
    ref,
  ) => {
    return tradeType === "buy" ? (
      <BuyLayout
        ref={ref}
        amount={amount}
        setAmount={setAmount}
        selectedNetwork={selectedNetwork}
        handleNetworkChange={handleNetworkChange}
        networkOptions={networkOptions}
        selectedFiatCurrency={selectedFiatCurrency}
        handleFiatCurrencyChange={handleFiatCurrencyChange}
        currencyOptions={currencyOptions}
        selectedToken={selectedCryptoCurrency}
        handleTokenChange={handleCryptoCurrencyChange}
        cryptoOptions={cryptoOptions}
        error={error}
        isPrimary={isPrimary}
        onSwap={onSwap}
      />
    ) : (
      <SellLayout
        ref={ref}
        amount={amount}
        setAmount={setAmount}
        selectedNetwork={selectedNetwork}
        handleNetworkChange={handleNetworkChange}
        networkOptions={networkOptions}
        selectedFiatCurrency={selectedFiatCurrency}
        handleFiatCurrencyChange={handleFiatCurrencyChange}
        currencyOptions={currencyOptions}
        selectedToken={selectedCryptoCurrency}
        handleTokenChange={handleCryptoCurrencyChange}
        cryptoOptions={cryptoOptions}
        error={error}
        isPrimary={isPrimary}
        onSwap={onSwap}
      />
    );
  },
);

QuickTradeInputs.displayName = "QuickTradeInputs";
